import { jwtDecode } from 'jwt-decode';
import type { JwtPayload, ParsedToken } from './types';

const normalizeToArray = (value: string | string[] | undefined): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

let cachedToken: string | null = null;
let cachedParsed: ParsedToken | null = null;

export function parseToken(token: string | null): ParsedToken | null {
  if (!token) return null;

  if (token === cachedToken && cachedParsed) {
    return cachedParsed;
  }

  try {
    const decoded = jwtDecode<JwtPayload>(token);

    const parsed: ParsedToken = {
      userId: decoded.sub,
      email: decoded.email,
      name: decoded.name,
      firstName: decoded.given_name?.trim() ?? '',
      lastName: decoded.family_name?.trim() ?? '',
      jti: decoded.jti,
      currentOrganisationId: decoded.current_organisation_id,
      currentOrganisationName: decoded.current_organisation_name,
      organisationIds: normalizeToArray(decoded.allowed_organisation_ids),
      roles: normalizeToArray(decoded.role),
      permissions: normalizeToArray(decoded.permission),
      expiresAt: new Date(decoded.exp * 1000),
      isFixedPasscodeSession: decoded.is_fixed_passcode_session === 'true',
      isInvoiceOnlySession: decoded.is_invoice_only_session === 'true',
      sessionId: decoded.session_id,
    };

    cachedToken = token;
    cachedParsed = parsed;

    return parsed;
  } catch (error) {
    console.error('Failed to parse token:', error);
    return null;
  }
}

export function getPermissionsFromToken(token: string | null): string[] {
  const parsed = parseToken(token);
  return parsed?.permissions ?? [];
}

export function getRolesFromToken(token: string | null): string[] {
  const parsed = parseToken(token);
  return parsed?.roles ?? [];
}

export function isTokenExpired(token: string | null): boolean {
  const parsed = parseToken(token);
  if (!parsed) return true;
  return parsed.expiresAt < new Date();
}

export function hasRole(token: string | null, role: string): boolean {
  const roles = getRolesFromToken(token);
  return roles.includes(role);
}

export function isAdmin(token: string | null): boolean {
  return hasRole(token, 'Admin');
}

export function clearTokenCache(): void {
  cachedToken = null;
  cachedParsed = null;
}

/**
 * Extracts the JWT ID (jti) from a token.
 * This is a unique identifier for the token, useful for logging.
 */
export function getTokenIdentifier(token: string | null): string | null {
  const parsed = parseToken(token);
  return parsed?.jti ?? null;
}

/**
 * Extracts user information from a JWT token.
 */
export interface UserInfoFromToken {
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  userId?: string;
}

export function extractUserInfo(token: string | null): UserInfoFromToken | null {
  const parsed = parseToken(token);
  if (!parsed) return null;

  return {
    email: parsed.email,
    firstName: parsed.firstName || 'User',
    lastName: parsed.lastName || '',
    fullName: parsed.name || `${parsed.firstName} ${parsed.lastName}`.trim() || parsed.email,
    userId: parsed.userId,
  };
}

